<template>
  <BaseModal
    :open="visible"
    :title="$t('pwa.title')"
    size="sm"
    @close="emit('close')"
  >
    <div class="pwa-modal">
      <div class="pwa-modal__icon-shell">
        <span class="pwa-modal__icon" :style="{ '--nav-icon': installIcon }" aria-hidden="true" />
      </div>

      <h2 class="pwa-modal__title">{{ $t('pwa.title') }}</h2>

      <div class="pwa-modal__content">
        <div class="pwa-step">
          <div class="pwa-step__number">1</div>
          <div class="pwa-step__text">{{ step1 }}</div>
        </div>
        <div class="pwa-step">
          <div class="pwa-step__number">2</div>
          <div class="pwa-step__text">{{ step2 }}</div>
        </div>
      </div>

      <button class="pwa-modal__button" @click="emit('close')">
        {{ $t('common.confirm') }}
      </button>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import BaseModal from '@/shared/components/base/BaseModal.vue'
import { isIOS, isAndroid, isChrome, isSafari } from '@/shared/utils/browser'
import { cssIconUrl } from '@/shared/utils/assets'
import IconInstall from '@/assets/images/icons/sections/install.svg'

defineProps<{
  visible: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const { t } = useI18n()
const installIcon = cssIconUrl(IconInstall)

const step1 = computed(() => {
  if (isIOS()) return t('pwa.ios.step1')
  if (isAndroid()) return t('pwa.android.step1')
  if (isChrome() || !isSafari()) return t('pwa.desktop.step1')
  return t('pwa.generic.step1')
})

const step2 = computed(() => {
  if (isIOS()) return t('pwa.ios.step2')
  if (isAndroid()) return t('pwa.android.step2')
  if (isChrome() || !isSafari()) return t('pwa.desktop.step2')
  return t('pwa.generic.step2')
})
</script>

<style scoped lang="scss">
.pwa-modal {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.pwa-modal__icon-shell {
  width: 64px;
  height: 64px;
  border-radius: var(--p-radius-20);
  background: linear-gradient(135deg, var(--ds-accent) 0%, var(--ds-accent-light) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  box-shadow: 0 8px 16px var(--ds-accent-soft-20);

}

.pwa-modal__icon {
  width: 32px;
  height: 32px;
  background-color: var(--ds-text-on-accent);
  mask: var(--nav-icon) no-repeat center / contain;
  -webkit-mask: var(--nav-icon) no-repeat center / contain;
}

.pwa-modal__title {
  font-size: var(--ds-text-size-h-md);
  font-weight: var(--ds-weight-bold);
  color: var(--ds-text);
  margin: 0 0 32px;
}

.pwa-modal__content {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 32px;
}

.pwa-step {
  display: flex;
  align-items: center;
  gap: 16px;
  text-align: left;
  background: var(--ds-overlay-04);
  padding: 16px;
  border-radius: var(--ds-radius-card-lg);
  border: 1px solid var(--ds-border);
}

.pwa-step__number {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--ds-accent);
  color: var(--ds-text);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--ds-text-size-body);
  font-weight: var(--ds-weight-bold);
  flex-shrink: 0;
}

.pwa-step__text {
  font-size: var(--p-text-15);
  line-height: 1.4;
  color: var(--ds-text-secondary);
  font-weight: var(--ds-weight-medium);
}

.pwa-modal__button {
  width: 100%;
  padding: 16px;
  border-radius: var(--p-radius-50);
  background: var(--ds-accent);
  color: var(--ds-text);
  border: none;
  font-size: var(--ds-text-size-body-lg);
  font-weight: var(--ds-weight-semibold);
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px var(--ds-accent-soft-20);

  &:hover {
    background: var(--ds-accent-light);
    transform: translateY(-1px);
    box-shadow: 0 6px 16px var(--ds-accent-soft-25);
  }

  &:active {
    transform: translateY(0);
  }
}
</style>
